"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useUser } from "@clerk/nextjs";
import { toast } from "sonner";
import posthog from "posthog-js";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  getDay,
  startOfWeek,
  endOfWeek,
  isWithinInterval,
} from "date-fns";
import { formatTimePST, formatTime24PST, getDateStringPST } from "@/lib/timezone";

export default function ClassCalendar() {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState("weekly"); // "weekly" or "calendar"
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [classesByDate, setClassesByDate] = useState({});
  const [allClasses, setAllClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedClass, setSelectedClass] = useState(null);
  const [userBookings, setUserBookings] = useState(new Set());
  const [bookingLoading, setBookingLoading] = useState(false);
  const [householdMembers, setHouseholdMembers] = useState([]);
  const [bookForClassId, setBookForClassId] = useState(null); // show "Book for: Me | ..." for this class

  // Memoize month boundaries to prevent infinite loops
  const monthStart = useMemo(() => startOfMonth(currentDate), [currentDate]);
  const monthEnd = useMemo(() => endOfMonth(currentDate), [currentDate]);
  const daysInMonth = useMemo(
    () => eachDayOfInterval({ start: monthStart, end: monthEnd }),
    [monthStart, monthEnd]
  );

  // Get the first day of the month to pad the calendar
  const firstDayOfWeek = getDay(monthStart);
  const paddingDays = Array.from({ length: firstDayOfWeek }, (_, i) => i);

  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));

  // Use month string as dependency instead of Date objects to prevent infinite loops
  const monthKey = useMemo(() => format(currentDate, "yyyy-MM"), [currentDate]);

  const fetchClasses = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const start = startOfMonth(currentDate);
      const endOfMonthDate = endOfMonth(currentDate);
      // Ensure end date includes the full last day (23:59:59.999 UTC)
      const endDate = new Date(
        Date.UTC(
          endOfMonthDate.getFullYear(),
          endOfMonthDate.getMonth(),
          endOfMonthDate.getDate(),
          23,
          59,
          59,
          999
        )
      );

      const res = await fetch(
        `/api/classes?start=${start.toISOString()}&end=${endDate.toISOString()}`,
        { cache: "no-store" }
      );
      if (!res.ok) throw new Error("Failed to load classes");
      const data = await res.json();
      const grouped = {};
      const classesList = [];
      (data.classes || []).forEach((cls) => {
        const date = new Date(cls.startTime);
        const key = getDateStringPST(date); // Use PST date string for grouping
        const classData = {
          id: cls.id,
          name: cls.className,
          instructor: cls.instructor,
          description: cls.description,
          time: formatTimePST(date), // 12-hour format with AM/PM in PST
          time24: formatTime24PST(date), // 24-hour format in PST for sorting
          dayOfWeek: format(date, "EEEE"), // Full day name (Monday, Tuesday, etc.)
          dayOfWeekShort: format(date, "EEE"), // Short day name (Mon, Tue, etc.)
          date: date,
          duration: cls.durationMinutes,
          capacity: cls.capacity,
          bookedCount: cls.bookedCount || 0,
          availableSpots: cls.availableSpots,
          location: cls.location,
          bookingEnabled: cls.bookingEnabled !== false, // Default to true if not set
        };
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(classData);
        classesList.push(classData);
      });
      Object.values(grouped).forEach((arr) =>
        arr.sort((a, b) => a.time24.localeCompare(b.time24))
      );
      setClassesByDate(grouped);
      setAllClasses(classesList);
    } catch (err) {
      console.error(err);
      setError("Unable to load classes. Please try again later.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthKey]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  // Fetch user's bookings and household members if logged in
  useEffect(() => {
    if (user) {
      fetchUserBookings();
      fetch("/api/household-members", { cache: "no-store" })
        .then((res) => res.ok ? res.json() : { householdMembers: [] })
        .then((data) => setHouseholdMembers(data.householdMembers || []))
        .catch(() => setHouseholdMembers([]));
    }
  }, [user]);

  const fetchUserBookings = async () => {
    try {
      const res = await fetch("/api/bookings", { cache: "no-store" });
      if (res.ok) {
        const data = await res.json();
        // "You're booked" = booking for self (no household member)
        const selfBookingClassIds = new Set(
          (data.bookings || []).filter((b) => !b.householdMemberId).map((b) => b.classId)
        );
        setUserBookings(selfBookingClassIds);
      }
    } catch (err) {
      console.error("Failed to fetch user bookings:", err);
    }
  };

  const handleBookClass = async (classId, householdMemberId = null) => {
    if (!user) {
      // Redirect to sign in
      window.location.href = "/sign-in";
      return;
    }

    setBookingLoading(true);
    try {
      const res = await fetch("/api/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ classId, householdMemberId: householdMemberId || undefined }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to book class");
      }

      // Add to user bookings (if booked for self)
      if (!householdMemberId) {
        setUserBookings((prev) => new Set([...prev, classId]));
      }

      // Find the class details for tracking
      const bookedClass = allClasses.find((c) => c.id === classId);

      // Track class booked event in PostHog
      posthog.capture("class_booked", {
        class_id: classId,
        class_name: bookedClass?.name,
        instructor: bookedClass?.instructor,
        class_date: bookedClass?.date?.toISOString(),
        class_time: bookedClass?.time,
        duration_minutes: bookedClass?.duration,
      });

      // Refresh classes to update availability
      fetchClasses();

      toast.success("Class booked successfully!", {
        description: "You can view and manage your bookings in your dashboard.",
      });
    } catch (err) {
      toast.error("Unable to book class", {
        description: err.message || "Please try again later.",
      });
    } finally {
      setBookingLoading(false);
    }
  };

  const getClassesForDate = useCallback(
    (date) => {
      const dateKey = format(date, "yyyy-MM-dd");
      return classesByDate[dateKey] || [];
    },
    [classesByDate]
  );

  const selectedClasses = useMemo(
    () => getClassesForDate(selectedDate),
    [getClassesForDate, selectedDate]
  );

  const handlePrint = () => {
    window.print();
  };

  // Group classes by time slot for weekly schedule (current week only)
  const weeklySchedule = useMemo(() => {
    // Get current week boundaries (Monday to Friday)
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 1 }); // Monday
    const fridayEnd = new Date(weekStart);
    fridayEnd.setDate(weekStart.getDate() + 4); // Friday
    fridayEnd.setHours(23, 59, 59, 999);
    
    // Filter classes to current week
    const weekClasses = allClasses.filter(cls => {
      return isWithinInterval(cls.date, { start: weekStart, end: fridayEnd });
    });

    // Group by time slot
    const timeSlots = {};
    const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
    
    weekClasses.forEach(cls => {
      const timeKey = cls.time; // e.g., "3:30 PM"
      if (!timeSlots[timeKey]) {
        timeSlots[timeKey] = {
          time: timeKey,
          time24: cls.time24,
          classes: {}
        };
        weekDays.forEach(day => {
          timeSlots[timeKey].classes[day] = null;
        });
      }
      
      const dayName = cls.dayOfWeek;
      if (weekDays.includes(dayName)) {
        timeSlots[timeKey].classes[dayName] = cls;
      }
    });

    // Convert to array and sort by time
    return Object.values(timeSlots).sort((a, b) => a.time24.localeCompare(b.time24));
  }, [allClasses]);

  return (
    <div className="w-full max-w-6xl mx-auto print-container">
      <div className="bg-[#FFF8E7] dark:bg-gray-800 rounded-lg shadow border border-[#D4AF37] dark:border-gray-700 p-4 md:p-6 print-no-break">
        {/* Tabs */}
        <div className="flex border-b border-[#D4AF37] dark:border-gray-600 mb-6 no-print">
          <button
            onClick={() => {
              setActiveTab("weekly");
              // Track schedule viewed event in PostHog
              posthog.capture("schedule_viewed", {
                view_type: "weekly",
              });
            }}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === "weekly"
                ? "text-[#D4AF37] border-b-2 border-[#D4AF37]"
                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100"
            }`}
          >
            Weekly Schedule
          </button>
          <button
            onClick={() => {
              setActiveTab("calendar");
              // Track schedule viewed event in PostHog
              posthog.capture("schedule_viewed", {
                view_type: "calendar",
              });
            }}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === "calendar"
                ? "text-[#D4AF37] border-b-2 border-[#D4AF37]"
                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100"
            }`}
          >
            Calendar View
          </button>
        </div>

        {/* Weekly Schedule Tab */}
        {activeTab === "weekly" && (
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 md:mb-0">
                Weekly Schedule
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Week of {format(startOfWeek(new Date(), { weekStartsOn: 1 }), "MMMM d")} - {format(new Date(startOfWeek(new Date(), { weekStartsOn: 1 }).setDate(startOfWeek(new Date(), { weekStartsOn: 1 }).getDate() + 4)), "MMMM d, yyyy")}
              </p>
            </div>
            {loading ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                Loading schedule...
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-600 dark:text-red-400">
                {error}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white dark:bg-gray-800 rounded-lg shadow-md">
                  <thead>
                    <tr className="bg-[#D4AF37] text-black">
                      <th className="px-4 py-4 text-left font-bold border-b-2 border-black border-r border-black">Time</th>
                      <th className="px-4 py-4 text-center font-bold border-b-2 border-black">Monday</th>
                      <th className="px-4 py-4 text-center font-bold border-b-2 border-black">Tuesday</th>
                      <th className="px-4 py-4 text-center font-bold border-b-2 border-black">Wednesday</th>
                      <th className="px-4 py-4 text-center font-bold border-b-2 border-black">Thursday</th>
                      <th className="px-4 py-4 text-center font-bold border-b-2 border-black">Friday</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklySchedule.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic">
                          No classes scheduled for this week
                        </td>
                      </tr>
                    ) : (
                      weeklySchedule.map((timeSlot, index) => (
                        <tr 
                          key={timeSlot.time}
                          className="border-b border-gray-200 dark:border-gray-700"
                        >
                          <td className="px-4 py-4 font-semibold text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-700">
                            {timeSlot.time}
                          </td>
                          {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((day) => {
                            const cls = timeSlot.classes[day];
                            return (
                              <td 
                                key={day}
                                className={`px-4 py-4 text-center ${
                                  cls 
                                    ? "hover:bg-[#FFF8E7] dark:hover:bg-gray-700 transition-colors cursor-pointer" 
                                    : "text-gray-300 dark:text-gray-500"
                                }`}
                                onClick={() => cls && setSelectedClass(cls)}
                              >
                                {cls ? (
                                  <div>
                                    <div className="font-medium text-gray-900 dark:text-gray-100">{cls.name}</div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{cls.instructor}</div>
                                  </div>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Calendar View Tab */}
        {activeTab === "calendar" && (
          <>
            <div className="flex flex-col md:flex-row items-center justify-between mb-4 md:mb-6 gap-4 no-print">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
                Class Schedule
              </h2>
          <div className="flex items-center space-x-2 md:space-x-4 w-full md:w-auto justify-between md:justify-start">
            <button
              onClick={prevMonth}
              className="p-3 md:p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 transition-colors text-gray-900 dark:text-gray-100 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Previous month"
            >
              <svg
                className="w-6 h-6 md:w-5 md:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h3 className="text-base md:text-xl font-semibold text-gray-800 dark:text-gray-200 min-w-[140px] md:min-w-[200px] text-center">
              {format(currentDate, "MMMM yyyy")}
            </h3>
            <button
              onClick={nextMonth}
              className="p-3 md:p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 transition-colors text-gray-900 dark:text-gray-100 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Next month"
            >
              <svg
                className="w-6 h-6 md:w-5 md:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium transition-colors touch-manipulation min-h-[44px]"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            Print Schedule
          </button>
        </div>
        <div className="print-header print-block hidden mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Class Schedule - {format(currentDate, "MMMM yyyy")}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Printed on {format(new Date(), "MMMM d, yyyy 'at' h:mm a")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 calendar-print">
          {/* Calendar */}
          <div className="lg:col-span-2 calendar-print">
            <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div
                  key={day}
                  className="text-center text-xs md:text-sm font-semibold text-gray-600 dark:text-gray-400 py-2"
                >
                  <span className="hidden sm:inline">{day}</span>
                  <span className="sm:hidden">{day.substring(0, 1)}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1 md:gap-2">
              {paddingDays.map((_, index) => (
                <div key={`padding-${index}`} className="aspect-square bg-transparent dark:bg-gray-800/50 rounded-lg"></div>
              ))}
              {daysInMonth.map((day) => {
                const dayClasses = getClassesForDate(day);
                const isSelected = isSameDay(day, selectedDate);
                const isToday = isSameDay(day, new Date());
                const hasClasses = dayClasses.length > 0;

                return (
                  <button
                    key={day.toString()}
                    onClick={() => setSelectedDate(day)}
                    className={`
                      aspect-square p-1 md:p-2 rounded-lg border-2 transition-all text-xs md:text-sm touch-manipulation
                      bg-white dark:bg-gray-800
                      ${
                        isSelected
                          ? "border-blue-600 bg-[#FFF8E7] dark:bg-gray-700 text-blue-900 dark:text-blue-100"
                          : "border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700/70 active:bg-[#FFF8E7] dark:active:bg-gray-700"
                      }
                      ${
                        isToday && !isSelected
                          ? "border-blue-400 bg-[#FFF8E7] dark:bg-gray-700/80 dark:border-blue-500"
                          : ""
                      }
                      ${!isSameMonth(day, currentDate) ? "opacity-30" : ""}
                    `}
                  >
                    <div className="text-xs md:text-sm font-medium mb-0.5 md:mb-1 text-gray-900 dark:text-gray-100">
                      {format(day, "d")}
                    </div>
                    {hasClasses && (
                      <div className="flex flex-wrap gap-0.5 md:gap-1 justify-center">
                        {dayClasses.slice(0, 2).map((cls, idx) => (
                          <div
                            key={idx}
                            className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-[#D4AF37]"
                            title={cls.name}
                          />
                        ))}
                        {dayClasses.length > 2 && (
                          <div className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400">
                            +{dayClasses.length - 2}
                          </div>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected Date Classes */}
          <div className="lg:col-span-1 print-hidden">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 h-full min-h-[200px]">
              <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {format(selectedDate, "EEEE, MMMM d")}
              </h3>
              {loading ? (
                <div className="text-gray-500 dark:text-gray-400 text-center py-8 text-sm md:text-base">
                  Loading classes...
                </div>
              ) : error ? (
                <div className="text-red-600 text-center py-8 text-sm md:text-base">
                  {error}
                </div>
              ) : selectedClasses.length > 0 ? (
                <div className="space-y-3">
                  {selectedClasses.map((cls, index) => (
                    <button
                      key={cls.id ?? index}
                      onClick={() => setSelectedClass(cls)}
                      className="w-full bg-white dark:bg-gray-800 rounded-lg p-4 md:p-3 shadow-sm border border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:shadow-md active:bg-[#FFF8E7] transition-all text-left touch-manipulation min-h-[60px] md:min-h-0"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm md:text-base">
                            {cls.name}
                          </h4>
                          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Instructor: {cls.instructor}
                          </p>
                          {cls.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                              {cls.description}
                            </p>
                          )}
                          {cls.capacity !== null &&
                            cls.availableSpots !== null && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                {cls.availableSpots > 0
                                  ? `${cls.availableSpots}/${cls.capacity} spots left`
                                  : "Fully booked"}
                              </p>
                            )}
                        </div>
                        <div className="text-sm md:text-base font-medium text-[#D4AF37] text-right">
                          <div>{cls.time}</div>
                          {cls.duration && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {cls.duration} min
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                  <p className="text-sm md:text-base">
                    No classes scheduled for this day.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
          </>
        )}
      </div>

      {/* Class Details Modal */}
      {selectedClass && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6"
          onClick={() => setSelectedClass(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto transform transition-all animate-in slide-in-from-bottom sm:slide-in-from-bottom-0"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-2xl sm:rounded-t-2xl p-6 text-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-2">
                    {selectedClass.name}
                  </h3>
                  <div className="flex items-center gap-4 text-blue-100 text-sm">
                    <span className="flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {selectedClass.time}
                    </span>
                    {selectedClass.duration && (
                      <span className="flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {selectedClass.duration} min
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedClass(null)}
                  className="text-white hover:text-blue-100 active:text-blue-200 transition-colors p-2 rounded-full hover:bg-white/20 active:bg-white dark:bg-gray-800/30 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#FFF8E7] rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-[#D4AF37]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Instructor
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {selectedClass.instructor}
                  </p>
                </div>
              </div>

              {selectedClass.location && (
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Location
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {selectedClass.location}
                    </p>
                  </div>
                </div>
              )}

              {selectedClass.description && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    About This Class
                  </p>
                  <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                    {selectedClass.description}
                  </p>
                </div>
              )}

              {selectedClass.capacity !== null && (
                <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Capacity
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {selectedClass.availableSpots !== null
                        ? selectedClass.availableSpots > 0
                          ? `${selectedClass.availableSpots}/${selectedClass.capacity} spots left`
                          : "Fully booked"
                        : `${selectedClass.capacity} people`}
                    </p>
                    {selectedClass.availableSpots !== null &&
                      selectedClass.availableSpots > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {selectedClass.bookedCount || 0} booked
                        </p>
                      )}
                  </div>
                </div>
              )}

              {/* Book Button */}
              {user && selectedClass.bookingEnabled && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  {userBookings.has(selectedClass.id) ? (
                    <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
                      <svg
                        className="w-5 h-5 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-green-700 font-medium">
                        You're booked for this class
                      </span>
                    </div>
                  ) : selectedClass.availableSpots === null || selectedClass.availableSpots > 0 ? (
                    <div className="space-y-2">
                      {bookForClassId === selectedClass.id && householdMembers.length > 0 ? (
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-3 space-y-2">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Book for:</p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => { handleBookClass(selectedClass.id, null); setBookForClassId(null); }}
                              disabled={bookingLoading}
                              className="rounded bg-[#D4AF37] px-3 py-1.5 text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-[#b8960e] disabled:opacity-50"
                            >
                              Me
                            </button>
                            {householdMembers.map((m) => (
                              <button
                                key={m.id}
                                type="button"
                                onClick={() => { handleBookClass(selectedClass.id, m.id); setBookForClassId(null); }}
                                disabled={bookingLoading}
                                className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700/50 disabled:opacity-50"
                              >
                                {m.fullName}
                              </button>
                            ))}
                            <button
                              type="button"
                              onClick={() => setBookForClassId(null)}
                              className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => householdMembers.length > 0 ? setBookForClassId(selectedClass.id) : handleBookClass(selectedClass.id)}
                          disabled={bookingLoading}
                          className="w-full px-6 py-4 md:py-3 bg-[#D4AF37] text-white font-semibold rounded-lg hover:bg-[#FFD700] active:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 touch-manipulation min-h-[44px] text-base md:text-sm"
                        >
                      {bookingLoading ? (
                        <>
                          <svg
                            className="animate-spin h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Booking...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          Book This Class
                        </>
                      )}
                    </button>
                      )}
                    </div>
                  ) : (
                    <div className="px-4 py-3 bg-gray-100 border border-gray-200 dark:border-gray-700 rounded-lg text-center text-gray-600 dark:text-gray-400">
                      This class is fully booked
                    </div>
                  )}
                </div>
              )}

              {!user &&
                selectedClass.bookingEnabled &&
                (selectedClass.availableSpots === null || selectedClass.availableSpots > 0) && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <a
                      href="/sign-in"
                      className="w-full block px-6 py-4 md:py-3 bg-[#D4AF37] text-white font-semibold rounded-lg hover:bg-[#FFD700] active:bg-blue-800 transition-colors text-center touch-manipulation min-h-[44px] text-base md:text-sm"
                    >
                      Sign in to Book
                    </a>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
